import { useEffect, useState } from "react";
import type { CommandCenterSnapshot } from "../types/login.types";
import { SystemHeader } from "./SystemHeader";
import { SystemFooter } from "./SystemFooter";
import { MesNetwork } from "./MesNetwork";
import { ProductionOverviewCard } from "./ProductionOverviewCard";
import { WeatherStatusCard } from "./WeatherStatusCard";
import { ExchangeRatesCard } from "./ExchangeRatesCard";
import { FactoryIllustration } from "./FactoryIllustration";
import { ConstellationDecoration } from "./ConstellationDecoration";
import {
  fetchMainExchangeRates,
  type ExchangeRatesSnapshot,
} from "../../../services/exchangeService";
import styles from "./CommandCenterVisual.module.css";

interface CommandCenterVisualProps {
  snapshot: CommandCenterSnapshot;
  className?: string;
  showClock?: boolean;
}

export function CommandCenterVisual({
  snapshot,
  className,
  showClock = false,
}: CommandCenterVisualProps) {
  const [fx, setFx] = useState<ExchangeRatesSnapshot | null>(null);
  const [fxError, setFxError] = useState<string | null>(null);
  const [fxLoading, setFxLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setFxLoading(true);
      try {
        const rates = await fetchMainExchangeRates();
        if (!cancelled) {
          setFx(rates);
          setFxError(null);
        }
      } catch {
        if (!cancelled) {
          setFxError("환율을 불러오지 못했습니다.");
        }
      } finally {
        if (!cancelled) setFxLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={[styles.visual, className].filter(Boolean).join(" ")}>
      <div className={styles.gridBg} aria-hidden />

      <div className={styles.headerBar}>
        <SystemHeader
          systemStatus={snapshot.systemStatus}
          hideClock={!showClock}
        />
      </div>

      <div className={styles.constellationWrap} aria-hidden>
        <ConstellationDecoration />
      </div>

      <div className={styles.networkLayer}>
        <MesNetwork nodes={snapshot.networkNodes} />
      </div>

      <div className={styles.factoryWrap} aria-hidden>
        <FactoryIllustration />
      </div>

      <div className={styles.stage}>
        <ProductionOverviewCard
          className={styles.productionCard}
          data={snapshot.production}
        />

        <div className={styles.networkSpacer} aria-hidden />

        <ExchangeRatesCard
          className={styles.alertCard}
          snapshot={fx}
          loading={fxLoading}
          error={fxError}
        />

        <WeatherStatusCard className={styles.lineCard} />
      </div>

      <SystemFooter className={styles.footer} year={2026} />
    </div>
  );
}
